<template>
	<div id="app">
		<div v-if="!requiredResourcesAreReady || redirectInPending || fwfRouterPending" class="loader">
			<div class="db-spinner"></div>
		</div>
		<FunnelContainer v-if="requiredResourcesAreReady && !fwfRouterPending">
			<router-view />
			<LanguageModal v-if="showLanguageModal" />
		</FunnelContainer>
	</div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { defineFunnelByParams } from '@/funnels/funnels.ts'
import { isObLandingAliasPath } from '@/funnels/funnelUtils.ts'
import { mapState } from 'pinia'
import { useMainStore } from '@/store/main'
import { useCoreFeaturesStore } from '@/store/coreFeatures'
import { useLocationStore } from '@/store/location'
import { useStatusStore } from '@/store/status'
import { useUserFlagsStore } from '@/store/userFlags'

import config from '@/config'
import { getHostnameFromURL, getQueryParamsFromUrl } from '@/helpers/url'
import { filterInvalidKeys } from '@/helpers/utils'
import Jet from '@/modules/Jet'
import Sentry from '@/modules/Sentry'
import {
	enrolInFwfRouter,
	isFwfRouterEntrySurface,
	resolveFwfTreatmentDestination,
} from '@/modules/Fwf/services/fwfRouter'

import { useExperiments } from '@/composables/useExperiments'
import { useOnetrust } from '@/composables/useOnetrust'

import FunnelContainer from '@/components/FunnelContainer.vue'
import { hashSha256 } from '@/helpers/hash'

const LanguageModal = defineAsyncComponent(() => import('@/components/LanguageModal.vue'))

export default {
	name: 'App',

	components: {
		FunnelContainer,
		LanguageModal,
	},

	setup() {
		useExperiments()
		useOnetrust()
	},

	data: () => ({
		routeQueryParams: {},
		onboardingStartTs: new Date(),
	}),

	computed: {
		...mapState(useMainStore, {
			countryCode: 'getCountryCode',
			countryCodeUppercase: 'getCountryCodeUppercase',
			gdpr: 'getGdprStatus',
			currency: 'getCurrency',
			currencyUppercase: 'getCurrencyUppercase',
			showLanguageModal: (state) => state.languageModal.showModal,
		}),

		...mapState(useStatusStore, ['requiredResourcesAreReady']),
		...mapState(useLocationStore, {
			getQueryParam: 'getQueryParam',
			isEmptyInitialUtmParams: 'isEmptyInitialUtmParams',
			appAreaConfig: 'getAppAreaConfig',
			initialPath: 'getInitialPath',
			initialFunnelType: 'getInitialFunnelType',
			initialFunnelId: 'getInitialFunnelId',
			paywallRoute: 'getPaywallRoute',
			hostname: 'hostname',
			getPathname: 'getPathname',
		}),

		...mapState(useCoreFeaturesStore, {
			paltaWhiteList: 'FEAT_PALTA_WHITE_LIST',
			paltaBlackList: 'FEAT_PALTA_BLACK_LIST',
		}),

		...mapState(useMainStore, ['marketingDeduplicationEventId']),

		...mapState(useLocationStore, {
			initialUtmParams: 'initialUtmParams',
		}),

		redirectInPending() {
			return useStatusStore().resourceIsPending('redirect')
		},

		// Held while the FwF flow router decides, so the funnel a treatment-arm visitor is
		// leaving never mounts — its LandingView.created() would enrol the landing
		// experiments and fire OnboardingStart for a session that is about to move.
		fwfRouterPending() {
			return useStatusStore().resourceIsPending('fwfRouter')
		},
	},

	watch: {
		requiredResourcesAreReady: {
			handler(value) {
				if (!value) {
					return
				}

				// После загрузки geodata надо переопределить appArea чтобы применились overrides по country
				const appAreaConfig = defineFunnelByParams(
					{
						appArea: this.getQueryParam('area') ?? null,
						initialPath: this.initialPath,
						country: this.countryCode,
						domain: this.hostname,
					},
					this.$absmartly,
				)

				useLocationStore().setAppAreaConfig(appAreaConfig)

				this.initAnalyticDestinationPlugins()
				this.setAnalyticsContext()
				this.processUtmParams()
				useLocationStore().defineOrganicTrafficFlag()

				this.routeToFwfPreFunnel(appAreaConfig)
			},
		},

		'appAreaConfig.appArea': {
			handler(newValue, oldValue) {
				if (newValue === oldValue) {
					return
				}

				this.$analytic.setUserProperties({
					FunnelId: this.appAreaConfig.funnel.id,
					PaywallRoute: this.paywallRoute,
				})

				Jet.setContext({
					onboarding: {
						onboarding_funnel_id: this.appAreaConfig.funnel.id,
						onboarding_funnel_type: this.appAreaConfig.funnel.slug,
						onboarding_url_path: this.$route.path,
						onboarding_initial_funnel_id: this.initialFunnelId || this.appAreaConfig.funnel.id,
						onboarding_initial_funnel_type: this.initialFunnelType || this.appAreaConfig.funnel.slug,
						onboarding_start_ts: this.onboardingStartTs.getTime(),
					},
				})

				Sentry.setTag('funnelId', this.appAreaConfig.funnel.id)
				Sentry.setTag('funnelType', this.appAreaConfig.funnel.slug)
				Sentry.setTag('countryCode', this.countryCodeUppercase)
				Sentry.setTag('currencyCode', this.currencyUppercase)
			},

			immediate: true,
		},
	},

	async created() {
		const queryParams = getQueryParamsFromUrl(window.location.href)
		this.routeQueryParams = {
			referrer: getHostnameFromURL(document.referrer),
			url: window.location.href,
			...queryParams,
		}

		if (window.language === 'ar') {
			import('@/styles/rtl.scss')
		}

		useLocationStore().storeQueryParams()

		// Must run before any routed view mounts: SignIn tracks its first ScreenView from an
		// immediate watcher (before its created()), so a view-level setContext is always too late
		// (DEV-34876). getRawQueryParam falls back to the persisted params, which keeps the id
		// across full reloads and the auth0 redirect round-trip that strips the URL query.
		const cancellationSessionId = useLocationStore().getRawQueryParam('cancellation_session_id')
		if (cancellationSessionId) {
			Jet.setContext({ cancellation: { cancellation_session_id: cancellationSessionId } })
		}

		let routeWithParamsSync = this.$router.resolve(`${this.getPathname()}${window.location.search}`)

		// Vue Router 4: resolve() does not follow redirects (unlike Vue Router 3).
		// Manually follow redirect to get the target route's path and meta.
		const redirectTarget = routeWithParamsSync.matched[0]?.redirect
		if (redirectTarget) {
			if (typeof redirectTarget === 'string') {
				routeWithParamsSync = this.$router.resolve(`${redirectTarget}${window.location.search}`)
			} else {
				// Named or object redirect — resolve via router to get the correct path
				const resolved = this.$router.resolve(redirectTarget)
				routeWithParamsSync = this.$router.resolve(`${resolved.path}${window.location.search}`)
			}
		}

		// OB-engine лендинг (DEV-34114) живёт на alias funnel-роута — meta.isLanding у его
		// записи нет, «это лендинг-вход» вычисляется через isObLandingAliasPath от appAreaConfig.
		// Конфиг резолвится внутри storeInitialPath, поэтому сначала резолвим путь без force…
		await useLocationStore().storeInitialPath({ path: routeWithParamsSync.path })

		// Alias-вход = лендинг: экраны лендинга рендерятся первыми (OnboardingView, легаси-
		// fallback решает useLegacyLandingFallback), трекинг должен стартовать выключенным
		// (tracking_enabled: false, включается обратно на первом не-лендинг-экране —
		// onScreenChanged в OB-рантайме), а повторный заход с utm — refresh-session, ровно
		// как у легаси-лендингов с meta.isLanding.
		const isLanding =
			Boolean(routeWithParamsSync.meta.isLanding) || isObLandingAliasPath(this.appAreaConfig, routeWithParamsSync.path)

		const isRefreshSession = Boolean(
			isLanding && (routeWithParamsSync.query.utm_source || routeWithParamsSync.redirectedFrom),
		)

		if (isRefreshSession) {
			// …а при refresh-session перерезолвливаем конфиг с force (прежняя семантика force:
			// повторный лендинг-заход должен учесть свежие query-параметры поверх persisted-конфига).
			// Повторный вызов безопасен: defineFunnelByParams детерминирован (чистые lookup'ы
			// + overrides), так что двойной резолв идемпотентен.
			await useLocationStore().storeInitialPath({ force: true, path: routeWithParamsSync.path })
		}

		if (isLanding) {
			// We store initial funnel type to be able to track CRs for gender selection funnels.
			// Атрибуция ставится на ЛЮБОМ лендинг-входе, не только при refresh-session — паритет
			// легаси-LandingView.created, который делал это безусловно (у OB-лендинга LandingView
			// не монтируется, и без этого свежий organic-вход слал бы initial_funnel_type: null
			// в каждом Amplitude-событии). Для легаси-лендингов LandingView.created как и раньше
			// перезапишет теми же значениями.
			useLocationStore().setInitialFunnelType(this.appAreaConfig.funnel.slug)
			useLocationStore().setInitialFunnelId(this.appAreaConfig.funnel.id)
		}

		this.setParamsToGTMDataLayer({ isLanding })
	},

	methods: {
		setParamsToGTMDataLayer({ isLanding }) {
			window.dataLayer?.push({
				event_id: this.marketingDeduplicationEventId,
			})
			window.dataLayer?.push({
				event: 'updateGtagData',
				country_code_sha256: hashSha256(this.countryCode?.toUpperCase()),
				tracking_enabled: !Boolean(isLanding),
			})
		},

		// FwF flow router (DEV-35548). Everything here is synchronous and ordered so that a
		// visitor is exposed only once we know they belong in the experiment at all.
		routeToFwfPreFunnel(appAreaConfig) {
			// Entry surface first, from the pathname — `$route` is not resolved yet at boot
			// (App mounts without `router.isReady()`), and this must also keep the router off
			// the paywall, mid-onboarding and `/fwf/*` itself. Before the enrol, so a visitor
			// on the wrong surface is never exposed.
			if (!isFwfRouterEntrySurface(appAreaConfig, this.hostname, this.getPathname())) {
				return
			}

			// The zero-overrides ABsmartly applied at ready-time were computed before
			// `defineOrganicTrafficFlag()` ran, so the flag is read here rather than trusted
			// through `assign()` — otherwise a first organic session enrols and redirects, and
			// is thrown back to the parent funnel on the next one.
			if (useUserFlagsStore().getIsOrganicTrafficOnboarding) {
				return
			}

			if (enrolInFwfRouter(this.$absmartly) !== 'treatment') {
				return
			}

			// Set before the first await, so the funnel being left never mounts.
			useStatusStore().setResourcePending('fwfRouter')

			// The chain is otherwise strictly serial — registry, then two cold chunks. Start
			// the view chunk now so it loads alongside the registry.
			import('@/modules/Fwf/FwfView.vue')

			// Not awaited: the boot watcher stays synchronous.
			void this.enterFwfPreFunnel(appAreaConfig)
		},

		async enterFwfPreFunnel(appAreaConfig) {
			const statusStore = useStatusStore()

			try {
				const destination = await resolveFwfTreatmentDestination(
					appAreaConfig,
					this.$absmartly,
					this.getQueryParam('fwf_router'),
					this.getQueryParam('fwf_variant'),
				)

				if (!destination) {
					return
				}

				// The arm the config named, beside the exposure — `Experiment - Exposure` carries
				// only name/iteration/variant, so a treatment holding several destinations would
				// otherwise be one indistinguishable bucket. The destination itself is already
				// `onboarding_initial_funnel_id`.
				this.$analytic.logEvent('Experiment - FwF Router', { fwfVariant: destination.id })

				// vue-router RESOLVES with a NavigationFailure when a navigation is aborted or
				// superseded, and REJECTS when a route chunk fails to load — the second is the
				// likelier cause of an exposed visitor left on the parent funnel, so both are
				// reported under the same tag.
				const failure = await this.$router.replace({
					name: 'fwf',
					params: { slug: destination.slug },
					query: this.$route.query,
				})

				if (failure) {
					this.reportFwfRouterRedirectFailure(destination, failure.type)
				}
			} catch (error) {
				this.reportFwfRouterRedirectFailure(null, error)
			} finally {
				statusStore.unsetResourcePending('fwfRouter')
			}
		},

		reportFwfRouterRedirectFailure(destination, detail) {
			Sentry.withScope((scope) => {
				scope.setTags({ fwfRouter: true })
				scope.setExtras({ slug: destination?.slug ?? null, variant: destination?.id ?? null, detail: String(detail) })
				scope.captureMessage('FwF router: exposed but the redirect did not complete')
			})
		},

		initAnalyticDestinationPlugins() {
			this.$analytic.initDestinationPlugins([
				{
					instanceOptions: {
						apiKey: config('AmplitudePaltaWeb'),
						serverUrl: 'https://telemetry.dataplatform.simple.life/v2/amplitude',
					},

					whiteList: this.paltaWhiteList,
					blackList: this.paltaBlackList,
				},
			])
		},

		setAnalyticsContext() {
			this.$analytic.setUserProperties({
				countryCode: this.countryCode,
				currency: this.currency,
				gdpr: this.gdpr,
				AppArea: this.appAreaConfig.appArea,
			})

			const appContext = filterInvalidKeys({
				app_currency: this.currency,
				app_gdpr_enabled: this.gdpr,
			})

			const deviceContext = filterInvalidKeys({
				device_country: this.countryCode,
			})

			Jet.setContext({ application: appContext, device: deviceContext })
		},

		processUtmParams() {
			if (!this.routeQueryParams.utm_source) {
				// Send this event only if any page is being loaded with `utm_source` parameter in the URL
				return
			}

			const referrer = getHostnameFromURL(document.referrer)
			const commonParams = {
				referrer: referrer,
				url: window.location.href,
				fbclid: this.routeQueryParams.fbclid,
				gclid: this.routeQueryParams.gclid,
			}

			window.dataLayer?.push({
				event: 'updateGtagData',
				utm_source: this.routeQueryParams.utm_source,
				utm_medium: this.routeQueryParams.utm_medium,
				utm_campaign: this.routeQueryParams.utm_campaign,
				utm_term: this.routeQueryParams.utm_term,
				utm_content: this.routeQueryParams.utm_content,
			})

			if (this.isEmptyInitialUtmParams) {
				useLocationStore().storeInitialUtmParams({
					initial_referrer: referrer,
					initial_utm_adgroup_id: this.routeQueryParams.adgroup_id,
					initial_utm_ad_id: this.routeQueryParams.ad_id,
					initial_utm_campaign_id: this.routeQueryParams.campaign_id,
					initial_utm_medium: this.routeQueryParams.utm_medium,
					initial_utm_source: this.routeQueryParams.utm_source,
					initial_utm_campaign: this.routeQueryParams.utm_campaign,
					initial_utm_content: this.routeQueryParams.utm_content,
					initial_utm_term: this.routeQueryParams.utm_term,
				})
			}

			this.$analytic.logEvent(
				'UtmParamsParsingDone',
				filterInvalidKeys({
					...commonParams,
					adgroup_id: this.routeQueryParams.adgroup_id,
					ad_id: this.routeQueryParams.ad_id,
					campaign_id: this.routeQueryParams.campaign_id,
					utm_gender: this.routeQueryParams.utm_gender,
					utm_medium: this.routeQueryParams.utm_medium,
					utm_source: this.routeQueryParams.utm_source,
					utm_campaign: this.routeQueryParams.utm_campaign,
					utm_content: this.routeQueryParams.utm_content,
					utm_term: this.routeQueryParams.utm_term,
					initial_referrer: this.initialUtmParams.initial_referrer,
					initial_utm_adgroup_id: this.initialUtmParams.initial_utm_adgroup_id,
					initial_utm_ad_id: this.initialUtmParams.initial_utm_ad_id,
					initial_utm_campaign_id: this.initialUtmParams.initial_utm_campaign_id,
					initial_utm_medium: this.initialUtmParams.initial_utm_medium,
					initial_utm_source: this.initialUtmParams.initial_utm_source,
					initial_utm_campaign: this.initialUtmParams.initial_utm_campaign,
					initial_utm_content: this.initialUtmParams.initial_utm_content,
					initial_utm_term: this.initialUtmParams.initial_utm_term,
				}),
			)

			Jet.track(
				'UtmParamsParsingDone',
				filterInvalidKeys({
					...commonParams,
					adgroup_id: this.routeQueryParams.adgroup_id,
					ad_id: this.routeQueryParams.ad_id,
					campaign_id: this.routeQueryParams.campaign_id,
					utm_gender: this.routeQueryParams.utm_gender,
					utm_medium: this.routeQueryParams.utm_medium,
					utm_source: this.routeQueryParams.utm_source,
					utm_campaign: this.routeQueryParams.utm_campaign,
					utm_content: this.routeQueryParams.utm_content,
					utm_term: this.routeQueryParams.utm_term,
					initial_referrer: this.initialUtmParams.initial_referrer,
					initial_utm_adgroup_id: this.initialUtmParams.initial_utm_adgroup_id,
					initial_utm_ad_id: this.initialUtmParams.initial_utm_ad_id,
					initial_utm_campaign_id: this.initialUtmParams.initial_utm_campaign_id,
					initial_utm_medium: this.initialUtmParams.initial_utm_medium,
					initial_utm_source: this.initialUtmParams.initial_utm_source,
					initial_utm_campaign: this.initialUtmParams.initial_utm_campaign,
					initial_utm_content: this.initialUtmParams.initial_utm_content,
					initial_utm_term: this.initialUtmParams.initial_utm_term,
				}),
			)
		},
	},
}
</script>

<style lang="scss">
@import '@/styles/style.scss';

#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.loader {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--background-color);
	z-index: 9;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
